import {
  type ComponentProps,
  type FC,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router";

import { useQuery } from "@apollo/client/react";

import { faPaperclip } from "@fortawesome/pro-duotone-svg-icons/faPaperclip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { DocumentKindEnum, RecordReviewWorkflowDocument } from "@app_schema";

import { CaseStepBubbles } from "@styled/case_steps";

import {
  type CaseStepKey,
  type IntakeType,
  SECTION_PARAMS,
  caseSteps,
} from "./case_status_lines";
import {
  DraftingDefaultStart,
  DraftingTemplateOwner,
} from "./drafting_default_start";
import { GuidedDraftingPanel } from "./drafting_tab";
import { DRAFT_COPY, FINAL_REPORT_COPY } from "./final_report_pane";
import { GuidedBubble } from "./guided_bubble";
import {
  GuidedDictationPanel,
  GuidedReportPanel,
} from "./guided_upload_panels";
import { IntakeInterviewPane } from "./intake_interview/intake_interview_pane";
import { ProcessRecordsCard } from "./process_records_card";
import { type ProjectsDocuments } from "./project_documents";
import { SupportingDocumentsPane } from "./supporting_documents_pane";
import { useProjectDocumentSave } from "./use_project_document_save";

type GuidedProject = ComponentProps<typeof ProjectsDocuments>["project"];

// A bubble per step, in step order (Drafting and Intake interview while the
// org's flags are on). Supporting documents is a quiet link, deliberately not
// a bubble — it is a side errand, not one of the ways to start a case.
//
// Copy is ratified — §2.4 of the handoff spec. Do not paraphrase.
const BUBBLE_COPY: Record<
  CaseStepKey,
  { body: ReactNode; tag?: string; sub?: string }
> = {
  records: {
    tag: "Start here",
    body: (
      <>
        <strong className="font-semibold">No records yet.</strong> Upload the
        medical records and we&apos;ll organize them into a chronological
        summary.
      </>
    ),
  },
  intakeInterview: {
    sub: "Before the exam.",
    body: "Send the claimant a link. They answer the intake questions out loud, and you review the outline before the exam.",
  },
  dictation: {
    sub: "Available anytime.",
    body: "Turn a recording or exam notes into a clean Word transcript.",
  },
  drafting: {
    sub: "Available anytime.",
    body: "Set up your Word template once. The Drafting Assistant writes each section from the sources you pick.",
  },
  finalReport: {
    sub: "Available anytime.",
    body: "For after your evaluation — we check it for issues, then you examine it with the Report Assistant.",
  },
};

const PANEL_ID = "guided-empty-panel";
const DISCLOSURE_ID = "guided-empty-supporting";

/**
 * The zero-documents state for a case: a bubble per bucket that scopes, and the
 * matching upload surface — every one an existing production surface — inline
 * beneath the selected one. Once any document lands `documentsCount` flips and
 * the detail page renders `ProjectsDocuments`, so this has no populated states.
 */
export const GuidedEmptyState: FC<{
  project: GuidedProject;
  onSelectSection: (section: IntakeType) => void;
  onChangeEvalType: () => void;
}> = ({ project, onSelectSection, onChangeEvalType }) => {
  const bubbles = caseSteps(project).map((step) => ({
    ...BUBBLE_COPY[step.key],
    key: step.key,
    label: step.label,
    tone: step.tone,
  }));
  // `?section=` (Save on Edit case) opens on that bubble once, then the param
  // is dropped so a refresh behaves like a normal visit.
  const [searchParams, setSearchParams] = useSearchParams();
  const linked = SECTION_PARAMS.get(searchParams.get("section") ?? "");
  const opening = Math.max(
    bubbles.findIndex((bubble) => bubble.key === linked),
    0,
  );
  useEffect(() => {
    if (!searchParams.has("section")) return;

    const next = new URLSearchParams(searchParams);
    next.delete("section");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const [selected, setSelected] = useState<CaseStepKey>(bubbles[opening].key);
  const [supportingOpen, setSupportingOpen] = useState(false);
  // Focus is tracked apart from selection so arrowing does NOT swap the panel:
  // the Records panel is expensive to mount, and APG prescribes manual
  // activation exactly there. Arrows move focus; Enter/Space picks.
  const [focused, setFocused] = useState(opening);
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);

  const { data: workflowData } = useQuery(RecordReviewWorkflowDocument);
  const saveDocument = useProjectDocumentSave(project.id);

  // The handoff: attach, then select the matching section so that when
  // `documentsCount` flips the tabbed view opens on the bucket just uploaded —
  // upload a report, land on Final Report, not back on Records.
  const saveInto = (kind: DocumentKindEnum | null, section: IntakeType) =>
    async function save(blob: { signed_id: string }) {
      await saveDocument(blob, kind);
      onSelectSection(section);
    };

  const onKeyDown = (event: KeyboardEvent) => {
    const last = bubbles.length - 1;
    const next =
      event.key === "ArrowRight"
        ? (focused + 1) % bubbles.length
        : event.key === "ArrowLeft"
          ? (focused - 1 + bubbles.length) % bubbles.length
          : event.key === "Home"
            ? 0
            : event.key === "End"
              ? last
              : -1;
    if (next < 0) return;
    event.preventDefault();

    setFocused(next);
    tabs.current[next]?.focus();
  };

  const renderPanel = () => {
    if (supportingOpen)
      return (
        <SupportingDocumentsPane
          project={project}
          documents={project.supportingDocuments}
          save={saveInto(null, "supporting")}
        />
      );

    switch (selected) {
      case "records":
        return (
          <ProcessRecordsCard
            project={project}
            documents={project.documents}
            recordReview={project.recordReview}
            workflow={workflowData?.runnableWorkflow}
            id="stage-process-records"
            onChangeEvalType={onChangeEvalType}
          />
        );
      case "finalReport":
        return (
          <GuidedReportPanel
            copy={FINAL_REPORT_COPY}
            save={saveInto(DocumentKindEnum.FinalReport, "finalReport")}
          />
        );
      case "drafting":
        return (
          <GuidedDraftingPanel
            project={project}
            save={async (blob, kind, role) => {
              await saveDocument(blob, kind, role);
              onSelectSection("drafting");
            }}
            fallback={
              <GuidedReportPanel
                copy={DRAFT_COPY}
                save={saveInto(DocumentKindEnum.ReportDraft, "drafting")}
                secondary={<DraftingDefaultStart projectId={project.id} />}
                footnote={
                  <DraftingTemplateOwner clinician={project.user.name} />
                }
              />
            }
          />
        );
      case "intakeInterview":
        return <IntakeInterviewPane projectId={project.id} flat />;
      case "dictation":
        return (
          <GuidedDictationPanel
            // The server makes it audio or notes by the file.
            save={saveInto(DocumentKindEnum.AudioRecording, "dictation")}
          />
        );
    }
  };

  return (
    // Same footprint as the populated case detail: uncentered, uncapped. The
    // bubbles ARE the work surface, so they occupy exactly what replaces them.
    // The app runs full-bleed — a capped column here would be the only one.
    <div className="w-full">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-ink text-[17px] font-semibold tracking-[-0.015em]">
            What do you want to work on?
          </h2>
          <p className="text-muted mt-0.5 text-[12px]">
            This case is just getting started — records come first.
          </p>
        </div>

        <button
          id={DISCLOSURE_ID}
          type="button"
          onClick={() => setSupportingOpen((open) => !open)}
          aria-expanded={supportingOpen}
          aria-controls={PANEL_ID}
          className="text-muted shrink-0 text-[12px] hover:text-indigo-700 hover:underline"
        >
          <FontAwesomeIcon icon={faPaperclip} aria-hidden /> Supporting
          documents
        </button>
      </div>

      <CaseStepBubbles label="What do you want to work on?">
        {bubbles.map((bubble, index) => (
          <GuidedBubble
            key={bubble.key}
            ref={(node) => {
              tabs.current[index] = node;
            }}
            id={`guided-tab-${bubble.key}`}
            aria-controls={supportingOpen ? undefined : PANEL_ID}
            mark={{ tone: bubble.tone }}
            label={bubble.label}
            tag={bubble.tag}
            body={bubble.body}
            sub={bubble.sub}
            selected={!supportingOpen && selected === bubble.key}
            tabIndex={index === focused ? 0 : -1}
            onKeyDown={onKeyDown}
            onClick={() => {
              setSelected(bubble.key);
              setFocused(index);
              setSupportingOpen(false);
            }}
          />
        ))}
      </CaseStepBubbles>

      <div
        id={PANEL_ID}
        // The panel is shared by the tablist and the supporting-documents
        // disclosure. While the disclosure owns it, no tab is selected, so it
        // is a region named by that button rather than an ownerless tabpanel.
        role={supportingOpen ? "region" : "tabpanel"}
        aria-labelledby={
          supportingOpen ? DISCLOSURE_ID : `guided-tab-${selected}`
        }
        className="surface-resting mt-3 rounded-[18px] p-4"
      >
        {renderPanel()}
      </div>
    </div>
  );
};
